import { useEffect, useState } from "react";
import { Check, Loader2, Save, Store, Warehouse, WifiOff } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { InlineLoader } from "@/components/common/Loader";
import { OPS } from "@/constants/testIds";
import { toLatinDigits } from "@/lib/wmDigits";
import { SC_TIMEOUT_MS, fmt, labelOf, scError, scMessage } from "./scUtils";

const S = OPS.sc.setup;
// sc_core._INT_SETTINGS ranges; the server re-checks every value
const INT_SETTINGS = [
    ["open_days_before_end", 1, 7],
    ["close_days_after", 0, 10],
    ["reopen_max_days", 1, 60],
    ["max_quantity", 1, 10000000],
];
const BOOL_SETTINGS = ["show_values_to_counters"];

/** GET /sc/locations answers the user form's short shape; if it carries no
 *  `erp_store`, the board's rows (full location docs) fill it in. */
async function loadLocations(period) {
    const r = await api.get("/sc/locations");
    const list = Array.isArray(r.data) ? r.data : r.data?.locations || [];
    if (!list.length || list.every((l) => "erp_store" in l) || !period) return list;
    try {
        const b = await api.get("/sc/board", { params: { period } });
        const full = {};
        (b.data?.rows || []).forEach((row) => { if (row.location?.id) full[row.location.id] = row.location; });
        return list.map((l) => ({ ...l, erp_store: full[l.id]?.erp_store ?? l.erp_store ?? "" }));
    } catch {
        return list;
    }
}

function LocationRow({ loc, scope, onSaved }) {
    const { t, tn, lang } = useI18n();
    const [store, setStore] = useState(loc.erp_store || "");
    const [busy, setBusy] = useState(false);
    const [ok, setOk] = useState(false);
    useEffect(() => { setStore(loc.erp_store || ""); }, [loc.erp_store]);
    const KindIcon = loc.kind === "warehouse" ? Warehouse : Store;

    const patch = async (body) => {
        setBusy(true);
        setOk(false);
        try {
            const r = await api.patch(`/sc/locations/${loc.id}`, body, { timeout: SC_TIMEOUT_MS });
            setOk(true);
            onSaved({ ...loc, ...body, ...(r.data && r.data.id ? r.data : {}) });
            toast.success(t("stk_setup_location_saved"));
        } catch (e) {
            toast.error(scMessage(e, lang) || t("stk_admin_offline_error"));
            setStore(loc.erp_store || "");
        } finally {
            setBusy(false);
        }
    };
    // Enter and blur both save; a request already in flight wins
    const saveStore = () => {
        if (busy) return;
        const v = store.trim();
        if (v !== (loc.erp_store || "").trim()) patch({ erp_store: v });
    };

    return (
        <li data-testid={S.location(loc.id)} data-active={loc.is_active === false ? "false" : "true"}
            className="grid grid-cols-[1fr_auto] items-center gap-x-3 gap-y-2 py-3 sm:grid-cols-[minmax(0,1fr)_16rem_auto]">
            <div className="min-w-0">
                <p className="flex items-center gap-1.5 text-sm font-bold text-[color:var(--ink)]">
                    <KindIcon className="h-4 w-4 shrink-0 text-[color:var(--brand-brown)]" />
                    <span className="truncate">{tn(loc)}</span>
                </p>
                <p className="text-[11px] text-[color:var(--ink-faint)]">
                    {labelOf(scope?.labels?.kinds, loc.kind, lang)} · <bdi dir="ltr">{loc.code}</bdi>
                </p>
            </div>
            <label className="col-start-2 row-start-1 flex items-center gap-2 text-xs font-semibold text-[color:var(--ink-muted)] sm:col-start-3">
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : ok ? <Check className="h-3.5 w-3.5 text-[color:var(--success)]" /> : null}
                <Switch data-testid={S.locationActive(loc.id)} checked={loc.is_active !== false} disabled={busy}
                        onCheckedChange={(v) => patch({ is_active: !!v })} />
                <span className="hidden sm:inline">{t(loc.is_active === false ? "stk_admin_inactive" : "stk_admin_active")}</span>
            </label>
            <div className="col-span-2 sm:col-span-1 sm:col-start-2 sm:row-start-1">
                <Input data-testid={S.locationStore(loc.id)} value={store} dir="auto" maxLength={120} disabled={busy}
                       placeholder={t("stk_setup_erp_store")} aria-label={t("stk_setup_erp_store")}
                       onChange={(e) => { setStore(e.target.value); setOk(false); }}
                       onBlur={saveStore}
                       onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveStore(); } }}
                       className="h-10 text-base" />
            </div>
        </li>
    );
}

function toForm(settings) {
    const f = {};
    INT_SETTINGS.forEach(([k]) => { f[k] = settings?.[k] === undefined || settings?.[k] === null ? "" : String(settings[k]); });
    BOOL_SETTINGS.forEach((k) => { f[k] = !!settings?.[k]; });
    return f;
}

/** «الإعداد» (finance): the locations (ERP store, active) saved row by row, and
 *  the count settings — the window, the quantity cap, values for counters, the
 *  reopen limit. `onChanged` lets the section refresh its scope. */
export function SetupAdmin({ scope, onChanged }) {
    const { t, lang } = useI18n();
    const [locations, setLocations] = useState(null);
    const [failed, setFailed] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);
    const [settings, setSettings] = useState(scope?.settings || null);
    const [form, setForm] = useState(() => toForm(scope?.settings));
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let alive = true;
        setFailed(false);
        Promise.all([loadLocations(scope?.default_period), api.get("/sc/settings")])
            .then(([locs, s]) => {
                if (!alive) return;
                setLocations(locs);
                setSettings(s.data);
                setForm(toForm(s.data));
            })
            .catch((e) => {
                if (!alive) return;
                // nothing to show without the locations: always offer the retry box
                const msg = scMessage(e, lang);
                if (msg) toast.error(msg);
                setFailed(true);
            });
        return () => { alive = false; };
    }, [reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps

    const set = (k, v) => {
        setForm((f) => ({ ...f, [k]: v }));
        setErrors((e) => { if (!e[k]) return e; const n = { ...e }; delete n[k]; return n; });
    };
    const changed = {};
    INT_SETTINGS.forEach(([k]) => {
        if (form[k] !== "" && Number(form[k]) !== Number(settings?.[k])) changed[k] = parseInt(form[k], 10);
    });
    BOOL_SETTINGS.forEach((k) => { if (form[k] !== !!settings?.[k]) changed[k] = form[k]; });
    const dirty = Object.keys(changed).length > 0;

    const save = async () => {
        const local = {};
        INT_SETTINGS.forEach(([k, lo, hi]) => {
            const v = Number(form[k]);
            if (form[k] === "" || !Number.isInteger(v) || v < lo || v > hi) local[k] = fmt(t("stk_setup_err_range"), { min: lo, max: hi });
        });
        if (Object.keys(local).length) { setErrors(local); return; }
        setSaving(true);
        setErrors({});
        try {
            const r = await api.patch("/sc/settings", changed, { timeout: SC_TIMEOUT_MS });
            setSettings(r.data);
            setForm(toForm(r.data));
            toast.success(t("stk_setup_settings_saved"));
            onChanged && onChanged();
        } catch (e) {
            const d = scError(e);
            if (d?.field) setErrors({ [d.field]: lang === "ar" ? d.message_ar : d.message_en });
            toast.error(scMessage(e, lang) || t("stk_admin_offline_error"));
        } finally {
            setSaving(false);
        }
    };

    const onLocationSaved = (loc) => {
        setLocations((list) => (list || []).map((l) => (l.id === loc.id ? { ...l, ...loc } : l)));
        onChanged && onChanged();
    };

    if (failed) {
        return (
            <div data-testid={OPS.sc.offlineError}
                 className="flex items-center justify-between gap-2 rounded-[var(--radius-sm)] bg-[color:rgba(220,38,38,0.08)] px-3 py-2">
                <span className="flex items-center gap-1.5 text-xs font-semibold text-[color:var(--danger)]">
                    <WifiOff className="h-3.5 w-3.5" />{t("stk_admin_offline_error")}
                </span>
                <Button size="sm" variant="outline" data-testid={OPS.sc.retry} onClick={() => setReloadKey((k) => k + 1)}>
                    {t("stk_admin_retry")}
                </Button>
            </div>
        );
    }

    return (
        <div data-testid={S.view} className="space-y-3">
            <section className="rounded-[var(--radius-lg)] border border-[color:var(--border-soft)] bg-[color:var(--bg-surface)] p-3">
                <h3 className="text-sm font-bold text-[color:var(--ink)]">{t("stk_setup_locations_title")}</h3>
                <p className="text-[11px] text-[color:var(--ink-muted)]">{t("stk_setup_locations_hint")}</p>
                {locations === null ? <InlineLoader /> : locations.length ? (
                    <ul className="divide-y divide-[color:var(--border-soft)]">
                        {locations.map((l) => <LocationRow key={l.id} loc={l} scope={scope} onSaved={onLocationSaved} />)}
                    </ul>
                ) : (
                    <p className="py-8 text-center text-sm text-[color:var(--ink-muted)]">{t("stk_setup_no_locations")}</p>
                )}
            </section>

            <section className="space-y-3 rounded-[var(--radius-lg)] border border-[color:var(--border-soft)] bg-[color:var(--bg-surface)] p-3">
                <div>
                    <h3 className="text-sm font-bold text-[color:var(--ink)]">{t("stk_setup_settings_title")}</h3>
                    <p className="text-[11px] text-[color:var(--ink-muted)]">{t("stk_setup_settings_hint")}</p>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                    {INT_SETTINGS.map(([k, lo, hi]) => (
                        <label key={k} className="block min-w-0 space-y-1">
                            <span className="block text-xs font-bold text-[color:var(--ink)]">{t(`stk_setup_${k}`)}</span>
                            <Input data-testid={S.setting(k)} value={form[k]} dir="ltr" inputMode="numeric" autoComplete="off"
                                   onChange={(e) => set(k, toLatinDigits(e.target.value).replace(/[^\d]/g, ""))}
                                   className={`h-11 text-base tabular-nums ${errors[k] ? "border-[color:var(--danger)]" : ""}`} />
                            <span className="block text-[11px] leading-snug text-[color:var(--ink-faint)]">
                                {t(`stk_setup_${k}_hint`)} (<bdi dir="ltr">{lo}–{hi}</bdi>)
                            </span>
                            {errors[k] ? <span className="block text-[11px] font-semibold text-[color:var(--danger)]">{errors[k]}</span> : null}
                        </label>
                    ))}
                    {BOOL_SETTINGS.map((k) => (
                        <label key={k} className="flex items-start justify-between gap-3 rounded-[var(--radius-md)] border border-[color:var(--border-soft)] px-3 py-2.5 sm:col-span-2">
                            <span className="min-w-0">
                                <span className="block text-xs font-bold text-[color:var(--ink)]">{t(`stk_setup_${k}`)}</span>
                                <span className="block text-[11px] leading-snug text-[color:var(--ink-faint)]">{t(`stk_setup_${k}_hint`)}</span>
                                {errors[k] ? <span className="block text-[11px] font-semibold text-[color:var(--danger)]">{errors[k]}</span> : null}
                            </span>
                            <Switch data-testid={S.setting(k)} checked={!!form[k]} onCheckedChange={(v) => set(k, !!v)} />
                        </label>
                    ))}
                </div>
                <div className="flex justify-end">
                    <Button data-testid={S.save} onClick={save} disabled={!dirty || saving} className="h-11 gap-2 px-6 text-sm font-bold">
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                        {saving ? t("stk_admin_saving") : t("stk_admin_save")}
                    </Button>
                </div>
            </section>
        </div>
    );
}
